import { ZfLoadingSpinner } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEvents, useGeneralProjectionSettings, useProjectionCashEventSettings } from '../api';
import type { PanelSettingsDto } from '../api/generated/client';
import { CLIENT_OUTPUT_EVENT_YEAR_0_ID, CLIENT_OUTPUT_EVENT_YEAR_1_ID, CLIENT_OUTPUT_EVENT_YEAR_2_ID } from '../config/constants';
import { useClientOutputLifeEventImages } from '../hooks/useClientOutputLifeEventImages';
import type { RoadmapEvent } from '../interfaces/event';
import { TimeLineBar } from './TimeLineBar';
import { TimeLineColumn } from './TimeLineColumn';

export const ClientOutputLifeEventsScreen: FunctionComponent = () => {
    const { data: allEvents, isLoading: isLoadingEvents } = useEvents();
    const { data: generalSettings, isLoading: isLoadingGeneralSettings } = useGeneralProjectionSettings();
    const { data: cashEventSettings, isLoading: isLoadingEventSettings } = useProjectionCashEventSettings();
    const events = [...allEvents.lifeEvents, ...allEvents.maturities];

    const useClientOutput = useClientOutputLifeEventImages();

    if (isLoadingEvents || isLoadingGeneralSettings || isLoadingEventSettings || useClientOutput.pending) {
        return <ZfLoadingSpinner style={{ margin: 'auto' }} />;
    }

    const createYearEventMap = (startYear: number, events: RoadmapEvent[], range: number): Map<number, RoadmapEvent[]> => {
        const yearRange = Array.from({ length: range }, (_, i) => startYear + i);

        const yearEventMap = new Map<number, RoadmapEvent[]>();
        yearRange.forEach((year) => yearEventMap.set(year, []));

        events.forEach((event) => {
            const eventYear = new Date(event.dueDate ?? '').getFullYear();
            if (yearEventMap.has(eventYear)) {
                yearEventMap.get(eventYear)?.push(event);
            }
        });

        return yearEventMap;
    };

    const createYearRange = (startYear: number, range: number): number[] => {
        return Array.from({ length: range }, (_, i) => startYear + i);
    };

    const filterEventsByProjectionPeriod = (events: RoadmapEvent[], panelSettings: PanelSettingsDto, projectionPeriod: number): RoadmapEvent[] => {
        const currentDate = new Date();
        const startOfCurrentYear = new Date(currentDate.getFullYear(), 0, 1);
        const cutoffDate = new Date(currentDate.getFullYear() + projectionPeriod, 0, 1);

        return events
            .map((event) => {
                if (!event.isCashFlowRelevant) return event;
                if (!panelSettings.enabled) return undefined;

                const match = panelSettings?.itemsSettings.find((item) => {
                    return item.configurableKey === event.configurableKey && item.enabled;
                });
                if (!match) return undefined;

                return {
                    ...event,
                    signedAmount: match.amount,
                };
            })
            .filter((event) => event !== undefined)
            .filter((event) => {
                const eventDate = new Date(event.dueDate ?? '');

                return eventDate >= startOfCurrentYear && eventDate < cutoffDate;
            });
    };

    const projectionPeriod = generalSettings?.projectionPeriodYears ?? 0;

    const filteredEvents = filterEventsByProjectionPeriod(events, cashEventSettings!, projectionPeriod);

    const startYearPage0 = new Date().getFullYear();
    const startYearPage1 = new Date().getFullYear() + 10;
    const startYearPage2 = new Date().getFullYear() + 20;

    const eventsByYearsPage0 = createYearEventMap(startYearPage0, filteredEvents, 10);
    const eventsByYearsPage1 = createYearEventMap(startYearPage1, filteredEvents, 10);
    const eventsByYearsPage2 = createYearEventMap(startYearPage2, filteredEvents, 10);

    return (
        <div>
            <div className="timeline" id={CLIENT_OUTPUT_EVENT_YEAR_0_ID}>
                <TimeLineBar years={createYearRange(startYearPage0, 10)} />

                <div className={'timeline__panel'}>
                    {Array.from(eventsByYearsPage0.entries()).map(([, lifeEvents], index) => (
                        <TimeLineColumn key={`projection-period-0-column-${index}`} lifeEvents={lifeEvents} images={useClientOutput.data!} />
                    ))}
                </div>
            </div>

            {projectionPeriod > 10 && (
                <div className="timeline" id={CLIENT_OUTPUT_EVENT_YEAR_1_ID}>
                    <TimeLineBar years={createYearRange(startYearPage1, 10)} />

                    <div className={'timeline__panel'}>
                        {Array.from(eventsByYearsPage1.entries()).map(([, lifeEvents], index) => (
                            <TimeLineColumn key={`projection-period-10-column-${index}`} lifeEvents={lifeEvents} images={useClientOutput.data!} />
                        ))}
                    </div>
                </div>
            )}
            {projectionPeriod > 20 && (
                <div className="timeline" id={CLIENT_OUTPUT_EVENT_YEAR_2_ID}>
                    <TimeLineBar years={createYearRange(startYearPage2, 10)} />

                    <div className={'timeline__panel'}>
                        {Array.from(eventsByYearsPage2.entries()).map(([, lifeEvents], index) => (
                            <TimeLineColumn key={`projection-period-20-column-${index}`} lifeEvents={lifeEvents} images={useClientOutput.data!} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
